import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
// Api
import { GET_ARTICLES, GET_ARTICLE_DETAILS } from 'api/articles/queries';
// Types
import {
  GetArticles,
  GetArticlesVariables,
  GetArticles_getArticles_entities,
} from 'api/articles/types/GetArticles';
import {
  GetArticleDetails,
  GetArticleDetailsVariables,
  GetArticleDetails_getArticles_entities,
} from 'api/articles/types/GetArticleDetails';
// UI
import { errorNotification } from 'ui/Notification';

// getArticles rejects a limit above 100
const ARTICLES_PAGE_LIMIT = 100;

type UseAllArticles = {
  articles: GetArticles_getArticles_entities[];
  loading: boolean;
  reload: () => Promise<void>;
  loadArticleDetails: (
    id: string
  ) => Promise<GetArticleDetails_getArticles_entities>;
};

// getArticles cannot filter by type, so every article is loaded and split here
export const useAllArticles = (): UseAllArticles => {
  const client = useApolloClient();
  const [articles, setArticles] = useState<GetArticles_getArticles_entities[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(true);
  const latestLoadId = useRef<number>(0);
  const loadedArticles = useRef<GetArticles_getArticles_entities[]>([]);

  const fetchPage = useCallback(
    async (offset: number) => {
      const { data } = await client.query<GetArticles, GetArticlesVariables>({
        query: GET_ARTICLES,
        variables: { input: { limit: ARTICLES_PAGE_LIMIT, offset } },
        fetchPolicy: 'network-only',
      });

      if (!data) {
        throw new Error('Articles could not be loaded');
      }

      return data.getArticles;
    },
    [client]
  );

  const fetchAllArticles = useCallback(async () => {
    const firstPage = await fetchPage(0);
    const remainingOffsets: number[] = [];
    for (
      let offset = ARTICLES_PAGE_LIMIT;
      offset < firstPage.total;
      offset += ARTICLES_PAGE_LIMIT
    ) {
      remainingOffsets.push(offset);
    }
    const remainingPages = await Promise.all(remainingOffsets.map(fetchPage));

    // an article created while paging shifts the offsets, so drop repeats
    const seenIds = new Set<string>();
    return [firstPage, ...remainingPages]
      .flatMap((page) => page.entities)
      .filter(({ id }) => !seenIds.has(id) && Boolean(seenIds.add(id)));
  }, [fetchPage]);

  const reload = useCallback(async () => {
    const loadId = ++latestLoadId.current;
    setLoading(true);

    try {
      const allArticles = await fetchAllArticles();
      if (loadId === latestLoadId.current) {
        loadedArticles.current = allArticles;
        setArticles(allArticles);
      }
    } catch (error) {
      if (loadId === latestLoadId.current) {
        errorNotification((error as Error)?.message || 'Something went wrong');
      }
    } finally {
      if (loadId === latestLoadId.current) {
        setLoading(false);
      }
    }
  }, [fetchAllArticles]);

  // takes in articles added or removed since the last load, without the
  // table's loading state
  const refreshQuietly = useCallback(async () => {
    const loadId = latestLoadId.current;
    const allArticles = await fetchAllArticles();
    if (loadId === latestLoadId.current) {
      loadedArticles.current = allArticles;
      setArticles(allArticles);
    }
  }, [fetchAllArticles]);

  const fetchDetailsInListPosition = useCallback(
    async (id: string) => {
      const offset = loadedArticles.current.findIndex(
        (article) => article.id === id
      );
      if (offset < 0) {
        return null;
      }

      const { data } = await client.query<
        GetArticleDetails,
        GetArticleDetailsVariables
      >({
        query: GET_ARTICLE_DETAILS,
        variables: { input: { limit: 1, offset } },
        fetchPolicy: 'network-only',
      });
      const details = data?.getArticles.entities[0];

      return details?.id === id ? details : null;
    },
    [client]
  );

  // getArticles has no id filter, so an article's details are read from its
  // position in the list
  const loadArticleDetails = useCallback(
    async (id: string) => {
      const details = await fetchDetailsInListPosition(id);
      if (details) {
        return details;
      }

      await refreshQuietly();
      const movedDetails = await fetchDetailsInListPosition(id);
      if (movedDetails) {
        return movedDetails;
      }

      throw new Error('This article could not be found anymore');
    },
    [fetchDetailsInListPosition, refreshQuietly]
  );

  useEffect(() => {
    reload();

    return () => {
      latestLoadId.current += 1;
    };
  }, [reload]);

  return { articles, loading, reload, loadArticleDetails };
};
